// @ts-strict-ignore
import uniq from 'lodash/uniq';
import { createAsyncThunk, unwrapResult } from '@reduxjs/toolkit';
import {
  ApplicationTypesMap,
  JourneyApplicationStateTypesMap,
  JourneyNodeTypesMap,
} from '@usealloy/typegen';

import { JA_TERMINAL_OUTCOME_STATES } from '~/application-review/constants';
import {
  formatApplication,
  getOutcomeOptionsByReviewNodeId,
} from '~/application-review/formatters';
import { getEOJReconWfNodeOutcomes } from '~/application-review/redux/utils';
import { OUTCOMES_API_MAX_LIMIT } from '~/dashboard/views/settings/outcomes/hooks';
import { convertToCamel, typhonAjaxFetch } from '~/helpers/api';
import apiCalls from '~/helpers/apiCalls';
import { typhonRoutes } from '~/helpers/routes';
import type { FeatureFlags } from '~/helpers/typescript';
import {
  CHECK_JOURNEY_ALERT_STATUS_ATTEMPTS,
  CHECK_JOURNEY_ALERT_STATUS_INTERVAL,
} from '~/investigations/constants';
import { investigationsErrorHandler } from '~/investigations/utils';
import type { State } from '~/store';

import { formatInvestigation } from '../formatters';
import type {
  AlertJourneyStep,
  InvestigationViewAlert,
  OutcomeOptionsByReviewNodeId,
  ReasonOptionsByOutcomeId,
} from '../types';
import {
  assignAlertAgent,
  assignInvestigationAgent,
  completeAlertReview as completeAlertReviewCall,
  completeAlertManualReview as submitAlertManualReview,
  fetchAssignableAgents,
  fetchInvestigation,
  fetchInvestigationsQueue,
  fetchInvestigationsQueueSavedSearches,
  fetchInvestigationSummary,
  fetchInvestigationEvidence,
  unassignInvestigation,
  unassignAlertAgent,
  fetchInvestigationsQueueFilters,
  fetchTagsAndOutcomes,
  processTags,
} from './helpers';
import {
  mergeAlertManualReview,
  mergeDocumentTypes,
  mergeInvestigationsQueue,
  mergeInvestigationsQueueError,
  mergeInvestigationsQueueFilters,
  mergeInvestigationQueueTagsOutcomeReasons,
  mergeInvestigationsQueueSavedSearches,
  mergeInvestigation,
  mergeInvestigationReviewsByToken,
  mergeInvestigationSummary,
  mergeInvestigationEvidence,
  mergeInvestigationViewError,
  mergeJourneyAlert,
  mergeJourneyAlertAssignableAgents,
  mergeSecondaryInvestigationData,
  mergeTimelineItems,
  mergeUnassignInvestigationAgent,
  mergeAssignableAgents,
  mergeAssignedAgent,
  mergeAlertAssignedAgent,
  mergeAlertUnassignedAgent,
  mergeInvestigationFetched,
} from './slice';
import type {
  AssignableAgentsRequest,
  AssignAgentRequest,
  AssignAlertAgentRequest,
  CompleteAlertReviewRequest,
  CompleteAlertManualReviewRequest,
  FormatInvestigationAlert,
  InvestigationRequest,
  InvestigationsQueueRequest,
  InvestigationJourneyAlertsRequest,
  InvestigationJourneyAlertRequest,
  LoadAlertManualReview,
  LoadNewInvestigationAlertsRequest,
  RefreshInvestigationAlert,
  SecondaryInvestigationDataRequest,
  UnassignInvestigation,
  UnassignAlertAgentRequest,
  JourneyAlertAssignableAgentsRequest,
  BulkRefreshInvestigationAlert,
  InvestigationSummaryRequest,
  InvestigationEvidenceRequest,
} from './types';

const {
  documents: { getDocumentTypes },
  investigations: {
    alerts: {
      getData: getAlertData,
    },
  },
  journeys: {
    alerts: {
      getManualReview,
    },
  },
  journeyActions: { getJourneyActions },
  settings: { getOutcomes, getReviewReasons },
} = apiCalls;

export const loadInvestigationsQueue = createAsyncThunk(
  'investigations/loadInvestigationsQueue',
  async (
    {
      featureFlags,
      queryParams,
      typhonDomain,
    }: InvestigationsQueueRequest,
    { dispatch },
  ) => {
    try {
      const investigations = await fetchInvestigationsQueue({
        queryParams: {
          ...queryParams,
          ...(featureFlags?.investigationsQueueV2 && {
            useNewSearch: true,
          }),
        },
        typhonDomain,
      });
      dispatch(mergeInvestigationsQueue(investigations));
    } catch (error) {
      investigationsErrorHandler({
        error,
        extra: {
          queryParams,
          typhonDomain,
        },
        message: 'Error fetching Investigations',
      });
      dispatch(mergeInvestigationsQueueError(error));
    }
  },
);

export const loadTagsAndOutcomeReasons = createAsyncThunk(
  'investigations/loadTagsAndOutcomes',
  async (
    {
      typhonDomain,
    }: {
      typhonDomain: string;
    },
    { dispatch },
  ) => {
    try {
      const tagsAndOutcomes = await fetchTagsAndOutcomes({
        typhonDomain,
      });
      const convertedTags = processTags(tagsAndOutcomes);
      dispatch(mergeInvestigationQueueTagsOutcomeReasons({
        payload: convertedTags,
      }));
    } catch (error) {
      investigationsErrorHandler({
        error,
        extra: {
          typhonDomain,
        },
        message: 'Error fetching Investigations',
      });
    }
  },
);

export const loadInvestigationsQueueFilters = createAsyncThunk(
  'investigations/loadInvestigationsQueue',
  async (
    {
      typhonDomain,
      featureFlags,
    }: {
      typhonDomain: string;
      featureFlags: Partial<FeatureFlags>;
    },
    { dispatch },
  ) => {
    try {
      const isEventLifecyclesPhaseThreeEnabled = Boolean(featureFlags?.eventLifecyclesPhaseThree);

      const [
        { filters },
        { outcomes },
      ] = await Promise.all([
        fetchInvestigationsQueueFilters({
          typhonDomain,
        }),
        getOutcomes({
          typhonDomain,
          options: {
            numRowsPerPage: OUTCOMES_API_MAX_LIMIT,
            q: {
              includeEventStatusOutcomes: isEventLifecyclesPhaseThreeEnabled,
            },
          },
        }),
      ]);

      if (outcomes?.length > 0) {
        dispatch(mergeSecondaryInvestigationData({ outcomes }));
      }

      dispatch(mergeInvestigationsQueueFilters({
        payload: convertToCamel(filters),
      }));
    } catch (error) {
      investigationsErrorHandler({
        error,
        extra: {
          typhonDomain,
        },
        message: 'Error fetching Investigations',
      });
    }
  },
);

export const loadInvestigationsQueueSavedSearches = createAsyncThunk(
  'investigations/loadInvestigationQueueSavedSearches',
  async (_, { dispatch }) => {
    try {
      const data = await fetchInvestigationsQueueSavedSearches();
      dispatch(mergeInvestigationsQueueSavedSearches({ searches: data }));
    } catch (error) {
      investigationsErrorHandler({
        error,
        message: 'Error fetching Investigations saved searches',
      });
    }
  },
);

export const loadInvestigation = createAsyncThunk(
  'investigations/loadInvestigation',
  async (
    {
      navigate = null,
      investigationToken,
      isEmployee = false,
      typhonDomain,
      featureFlags,
    }: InvestigationRequest,
    { dispatch },
  ) => {
    try {
      const investigation = await fetchInvestigation({
        investigationToken,
        typhonDomain,
        toggleInvestigationsSandboxFeatureFlag: featureFlags.toggleInvestigationsSandbox,
      });

      /**
       * TODO: Remove https://team-alloy.atlassian.net/browse/OPERATE1-1710
       *
       * Special handling for Suncoast superentity causing issues loading the Investigation view
       * Cause of incident https://firstmilegroup.slack.com/archives/C07GZ68SQN7
       */
      if (
        !isEmployee
        // eslint-disable-next-line no-underscore-dangle
        && investigation?._embedded?.entities?.['B-HWvi63xbBoeQLwmBROMw']
        && navigate
      ) {
        navigate('/v3/dashboard/error/404');
        return null;
      }

      const formattedInvestigation = formatInvestigation(investigation);
      dispatch(mergeInvestigation(formattedInvestigation));
      const investigationReviewsByToken = (
        (formattedInvestigation.investigationReviews ?? []).reduce(
          (acc, review) => {
            acc[review.uiReviewToken] = review;
            return acc;
          }, {},
        )
      );
      dispatch(mergeInvestigationReviewsByToken(investigationReviewsByToken));
      dispatch(mergeTimelineItems());

      return formattedInvestigation;
    } catch (error) {
      investigationsErrorHandler({
        error,
        extra: {
          investigationToken,
          typhonDomain,
        },
        message: 'Error fetching Investigation',
      });
      dispatch(mergeInvestigationViewError(error));
    }

    return null;
  },
);

export const loadInvestigationSummary = createAsyncThunk(
  'investigations/loadInvestigationSummary',
  async (
    {
      investigationToken,
      typhonDomain,
    }: InvestigationSummaryRequest,
    { dispatch },
  ) => {
    try {
      const investigationSummary = await fetchInvestigationSummary({
        investigationToken,
        typhonDomain,
      });
      dispatch(mergeInvestigationSummary(investigationSummary));
    } catch (error) {
      investigationsErrorHandler({
        error,
        extra: {
          investigationToken,
          typhonDomain,
        },
        message: 'Error fetching Investigation Summary',
      });
      dispatch(mergeInvestigationViewError(error));
    }

    return null;
  },
);

export const loadInvestigationEvidence = createAsyncThunk(
  'investigations/loadInvestigationEvidence',
  async (
    {
      investigationToken,
      typhonDomain,
    }: InvestigationEvidenceRequest,
    { dispatch },
  ) => {
    try {
      const evidenceData = await fetchInvestigationEvidence({
        investigationToken,
        typhonDomain,
      });
      dispatch(mergeInvestigationEvidence(evidenceData));
      dispatch(mergeTimelineItems());
    } catch (error) {
      investigationsErrorHandler({
        error,
        extra: {
          investigationToken,
          typhonDomain,
        },
        message: 'Error fetching Investigation Evidence',
      });
      dispatch(mergeInvestigationViewError(error));
    }

    return null;
  },
);

export const loadAlertManualReview = createAsyncThunk(
  'investigations/loadAlertManualReview',
  async (
    {
      alertManualReviewToken,
      journeyApplicationToken,
      journeyToken,
      typhonDomain,
    }: LoadAlertManualReview,
    { dispatch },
  ) => {
    const alertManualReviewDetails = await getManualReview({
      alertManualReviewToken,
      journeyToken,
      journeyApplicationToken,
      typhonDomain,
    });

    dispatch(
      mergeAlertManualReview({
        ...convertToCamel(alertManualReviewDetails),
        outcomeIcon: alertManualReviewDetails?.outcome_icon_name ?? undefined,
        outcomeColor: alertManualReviewDetails?.outcome_color ?? undefined,
      }),
    );
  },
);

export const formatInvestigationAlert = createAsyncThunk(
  'investigations/formatInvestigationAlert',
  async (
    {
      alertData,
      isMultiEntityAlertsInInvestigationsEnabled,
      journeyApplicationToken,
      typhonDomain,
    }: FormatInvestigationAlert,
    {
      dispatch,
      getState,
    },
  ) => {
    const {
      investigations: {
        investigation: {
          journeyActions,
          journeyVersionsByToken,
          outcomes,
          reasons,
        },
      },
    } = getState() as State;

    try {
      const {
        _embedded: {
          events,
          investigation_alert: {
            assigned_agent: assignedAgent,
            event_type: eventType,
            source_workflow_type: sourceWorkflowType,
            device_ids: deviceIds,
          },
          investigation_alert_evaluations: evaluationData,
          investigation_alert_reviews: appendedAlertReviews = [],
          additional_investigations: additionalInvestigations = [],
        },
      } = alertData;

      const evaluationDataWithOutcomeCustomizations = evaluationData.map(evalData => ({
        ...evalData,
        outcomeIcon: evalData?.outcome_icon_name || null,
        outcomeColor: evalData?.outcome_color ? `#${evalData.outcome_color}` : null,
      }));
      /**
       * To prepare for a possible future where we allow multiple appended Alert
       * reviews on an Alert within an Investigation, the API returns reviews in an
       * `investigation_alert_reviews` array. However, we currently only support
       * appending one review and try to pull that data from the first array index here.
       */
      const {
        review_note: reviewNote,
        review_outcome: reviewOutcome,
        review_reason: reviewReason,
        reviewed_at: reviewedAt,
        reviewed_by: reviewedBy,
      } = (appendedAlertReviews?.[0] ?? {});

      const evaluationsByToken = evaluationDataWithOutcomeCustomizations.reduce(
        (acc, evaluation) => ({
          ...acc,
          [evaluation.evaluation_token]: convertToCamel(evaluation),
        }),
        {},
      );

      const formattedApplication = formatApplication({
        application: alertData,
        isMultiEntityAlertsInInvestigationsEnabled,
      });

      const { journeySteps } = formattedApplication;

      // Journey steps should be displayed in reverse-chronological order starting from bottom
      journeySteps.reverse();
      // Any steps with attached review should have data fetched from backend
      const journeyStepsWithReviewData = await Promise.all(
        journeySteps.map(async step => {
          if (step.reviews) {
            // Reviews should also be displayed in reverse chronological order, starting from bottom
            step.reviews.reverse();
            const journeyStepReviews = await Promise.all(
              step.reviews.map(async review => ({
                ...review,
                /**
                 * In some future state we will likely need to fetch and set legacy Journey Alert review data
                 * completed via Cases, but for MVP we will pull the data we can from the current JA event return
                 * as a graceful fallback.
                 *
                 * This also satisfies part of the UI while the Alert component triggers a fetch for the full
                 * data for the Alert Manual Review.
                 */
                outcome: review.status !== 'Pending' ? review.status : null,
                reviewedAt: review.visitedNodeAt,
              })),
            );
            step.reviews = journeyStepReviews;
            // Copy fetched data to the review object representing the most recent review
            // Should be first one in array after reverse() call above
            [step.review] = step.reviews;
          }
          return step;
        }),
      );

      let journeyVersion;
      const isTerminalState = JA_TERMINAL_OUTCOME_STATES.includes(
        formattedApplication.journeyApplicationState,
      );
      const hasAppendedReview = appendedAlertReviews.length > 0;
      if (isTerminalState && !hasAppendedReview) {
        const { journeyToken, journeyVersionNum } = formattedApplication;
        const currJourneyVersionVal = journeyVersionsByToken?.[journeyToken]?.[journeyVersionNum];
        if (currJourneyVersionVal) {
          journeyVersion = currJourneyVersionVal;
        } else {
          try {
            const journeyVersionData = await typhonAjaxFetch(`${typhonDomain}${typhonRoutes.journeys.getVersion(journeyToken, journeyVersionNum)}`);
            journeyVersion = journeyVersionData;
            dispatch(mergeSecondaryInvestigationData({
              journeyVersionsByToken: {
                [journeyToken]: {
                  [journeyVersionNum]: journeyVersionData,
                },
              },
            }));
          } catch (error) {
            investigationsErrorHandler({
              error,
              extra: {
                typhonDomain,
              },
              message: 'Error fetching journey version',
            });
            dispatch(mergeInvestigationViewError(error));
          }
        }
      }

      // This array will only be populated if the journey alert is in a terminal state
      const allowedJourneyOutcomes = uniq(
        journeyVersion?.nodes
          .filter(node => node.type === JourneyNodeTypesMap.OUTCOME)
          .map(node => node.config?.outcome),
      ) || [];

      const eojReconNode = (
        isMultiEntityAlertsInInvestigationsEnabled
        && journeyVersion?.terminal_reconciliation_workflow_node_id
      )
        ? journeyVersion.nodes.find(
          node => node.type === JourneyNodeTypesMap.RECONCILIATION_WORKFLOW
            && node.id === journeyVersion.terminal_reconciliation_workflow_node_id,
        )
        : null;

      const eojReconWfOutcomes = await getEOJReconWfNodeOutcomes({
        eojReconNode,
        /**
         * This util was originally created for multi-entity Application Journeys, so required
         * checking the recon WF in graph FF. Multi-entity Alerts are newer and will only support
         * recon logic using WFs, not legacy reconciliation conditions. So we can safely
         * pass true here.
         */
        isReconWorkflowInGraphEnabled: true,
        journeyApplicationState: formattedApplication.journeyApplicationState,
      });

      const outcomeOptionsByReviewNodeId = getOutcomeOptionsByReviewNodeId({
        allowedJourneyOutcomes,
        applicationEvents: events,
        eojReconWfOutcomes,
        isMultiEntityAlertsInInvestigationsEnabled,
        journeyActions,
        journeyType: formattedApplication.journeyType,
        /**
         * Investigations explicitly does not support legacy Approved/Denied outcomes for
         * ongoing type journeys. This means we don't have to load the journey version and
         * check the outcome nodes to set this param.
         */
        journeyVersionHasOldOutcomes: false,
        outcomes,
      }) as OutcomeOptionsByReviewNodeId;

      const reasonOptionsByOutcomeId = reasons
        .reduce((acc, {
          customer_outcome_id,
          customer_reason_id: id,
          default_note: defaultNote,
          reason,
        }) => {
          const outcomeId = customer_outcome_id || 'noId';
          if (!acc[outcomeId]) {
            acc[outcomeId] = [];
          }
          acc[outcomeId].push({ defaultNote, id, value: reason });
          return acc;
        }, {} as ReasonOptionsByOutcomeId);

      // eslint-disable-next-line @typescript-eslint/dot-notation
      const reasonsWithoutId = reasonOptionsByOutcomeId['noId'];
      if (reasonsWithoutId) {
        Object.keys(reasonOptionsByOutcomeId)
          .forEach(key => {
            if (key !== 'noId') {
              reasonOptionsByOutcomeId[key] = [
                ...reasonOptionsByOutcomeId[key],
                ...reasonsWithoutId,
              ].sort((a, b) => a?.label?.localeCompare(b?.label) || 0);
            }
          });
        outcomes.forEach(({ customer_outcome_id }) => {
          if (!reasonOptionsByOutcomeId[customer_outcome_id]) {
            reasonOptionsByOutcomeId[customer_outcome_id] = [...reasonsWithoutId];
          }
        });
      }

      const journeyAlertOutcomeReasons = evaluationData.reduce(
        (outcomeReasonsArray, evaluation) => {
          (evaluation.tags || []).forEach(tag => {
            if (tag.is_outcome_reason) {
              outcomeReasonsArray.push(tag);
            }
          });
          return outcomeReasonsArray;
        }, [],
      );

      const sourceWorkflowTypeDisplay = sourceWorkflowType === ApplicationTypesMap.WEBHOOK
        ? 'watchlist'
        : sourceWorkflowType;

      const entityAlertDataByToken = formattedApplication.childEntities?.reduce((acc, entity) => {
        const entityApplication = formattedApplication.entityApplications?.find(
          ea => ea.entityToken === entity.token,
        );

        acc[entity.token] = {
          ...entity,
          ...entityApplication,
        };

        return acc;
      }, {});

      const formattedAlert = {
        ...formattedApplication,
        additionalInvestigations,
        alertReview: reviewedBy ? {
          note: reviewNote,
          outcome: reviewOutcome,
          reason: reviewReason,
          reviewedAt,
          reviewedBy: convertToCamel(reviewedBy),
        } : null,
        alertType: eventType ?? sourceWorkflowTypeDisplay,
        assignedAgent: convertToCamel(assignedAgent),
        deviceIds: (deviceIds || []).filter(Boolean),
        entityAlertDataByToken,
        evaluationsByToken,
        eventType,
        journeyAlertOutcomeReasons,
        journeySteps: journeyStepsWithReviewData as AlertJourneyStep[],
        outcomeOptionsByReviewNodeId,
        reasonOptionsByOutcomeId,
        sourceWorkflowType: sourceWorkflowTypeDisplay,
      };

      dispatch(mergeJourneyAlert(formattedAlert));
      dispatch(mergeTimelineItems());
    } catch (error) {
      investigationsErrorHandler({
        error,
        extra: {
          journeyApplicationToken,
          typhonDomain,
        },
        message: 'Error formatting journey alert data in Investigation',
      });
      dispatch(mergeInvestigationViewError(error));
    }
  },
);

const waitForInvestigationAlertUpdate = async ({
  investigationToken,
  journeyApplicationToken,
  typhonDomain,
  attempts = 0,
}) => {
  if (attempts >= CHECK_JOURNEY_ALERT_STATUS_ATTEMPTS) {
    throw new Error(`Journey Alert hasn't reached a new paused state after ${attempts} attempts`);
  }

  const alertData = await getAlertData({
    investigationToken,
    journeyApplicationToken,
    typhonDomain,
  });

  if (alertData.status === JourneyApplicationStateTypesMap.RUNNING) {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve(
          waitForInvestigationAlertUpdate({
            investigationToken,
            journeyApplicationToken,
            attempts: attempts + 1,
            typhonDomain,
          }),
        );
      }, CHECK_JOURNEY_ALERT_STATUS_INTERVAL);
    });
  }

  return alertData;
};

export const refreshInvestigationAlert = createAsyncThunk(
  'investigations/refreshInvestigationAlert',
  async (
    {
      investigationToken,
      isMultiEntityAlertsInInvestigationsEnabled,
      journeyApplicationToken,
      typhonDomain,
    }: RefreshInvestigationAlert,
    { dispatch },
  ) => {
    const alertData = await waitForInvestigationAlertUpdate({
      investigationToken,
      journeyApplicationToken,
      typhonDomain,
    });

    await dispatch(formatInvestigationAlert({
      alertData,
      isMultiEntityAlertsInInvestigationsEnabled,
      journeyApplicationToken,
      typhonDomain,
    }));
  },
);

export const bulkRefreshInvestigationAlert = createAsyncThunk(
  'investigations/bulkRefreshInvestigationAlert',
  async (
    {
      investigationToken,
      isMultiEntityAlertsInInvestigationsEnabled,
      journeyApplicationTokens,
      typhonDomain,
    }: BulkRefreshInvestigationAlert,
    { dispatch },
  ) => {
    await Promise.all(journeyApplicationTokens.map(async journeyApplicationToken => {
      const alertData = await waitForInvestigationAlertUpdate({
        investigationToken,
        journeyApplicationToken,
        typhonDomain,
      });

      await dispatch(formatInvestigationAlert({
        alertData,
        isMultiEntityAlertsInInvestigationsEnabled,
        journeyApplicationToken,
        typhonDomain,
      }));
    }));
  },
);

export const loadInvestigationAlert = createAsyncThunk(
  'investigations/loadInvestigationAlert',
  async (
    {
      investigationToken,
      isMultiEntityAlertsInInvestigationsEnabled,
      evidence,
      typhonDomain,
    }: InvestigationJourneyAlertRequest,
    { dispatch },
  ) => {
    try {
      try {
        const alertData = await getAlertData({
          investigationToken,
          journeyApplicationToken: evidence.journeyApplicationToken,
          typhonDomain,
        });

        await dispatch(formatInvestigationAlert({
          alertData,
          isMultiEntityAlertsInInvestigationsEnabled,
          journeyApplicationToken: evidence.journeyApplicationToken,
          typhonDomain,
        }));
      } catch (error) {
        if (error.response.status !== 403) {
          throw error;
        }

        dispatch(mergeJourneyAlert({
          alertReview: null,
          alertUnavailable: true,
          assignedAgent: null,
          evaluationsByToken: {},
          journeyApplicationState: JourneyApplicationStateTypesMap.ERROR,
          journeySteps: [] as AlertJourneyStep[],
          outcomeOptionsByReviewNodeId: {},
          reasonOptionsByOutcomeId: {},
          journeyApplicationToken: evidence.journeyApplicationToken,
        } as InvestigationViewAlert));
        dispatch(mergeTimelineItems());
      }
    } catch (error) {
      investigationsErrorHandler({
        error,
        extra: {
          investigationToken,
          evidence,
          typhonDomain,
        },
        message: 'Error fetching single Investigation evidence (journey alert)',
      });
      dispatch(mergeInvestigationViewError(error));
    }
  },
);

export const loadInvestigationJourneyAlerts = createAsyncThunk(
  'investigations/loadInvestigationEvidences',
  async (
    {
      investigationToken,
      isMultiEntityAlertsInInvestigationsEnabled,
      journeyAlerts,
      typhonDomain,
    }: InvestigationJourneyAlertsRequest,
    { dispatch },
  ) => {
    try {
      await Promise.all(
        journeyAlerts?.map(evidence => (
          dispatch(loadInvestigationAlert({
            evidence,
            investigationToken,
            isMultiEntityAlertsInInvestigationsEnabled,
            typhonDomain,
          }))
        )),
      );
    } catch (error) {
      investigationsErrorHandler({
        error,
        extra: {
          journeyAlerts,
          typhonDomain,
        },
        message: 'Error fetching Investigation evidences (journey alerts)',
      });
      dispatch(mergeInvestigationViewError(error));
    } finally {
      await dispatch(mergeInvestigationFetched(true));
    }
  },
);

export const loadNewInvestigationAlerts = createAsyncThunk(
  'investigations/loadNewInvestigationAlerts',
  async (
    {
      featureFlags,
      investigationToken,
      typhonDomain,
    }: LoadNewInvestigationAlertsRequest,
    {
      dispatch,
      getState,
    },
  ) => {
    const {
      investigations: {
        investigation: {
          journeyAlertsByToken,
        },
      },
    } = getState() as State;

    const investigationThunk = await dispatch(loadInvestigation({
      investigationToken,
      typhonDomain,
      featureFlags,
    }));

    const investigation = unwrapResult(investigationThunk);

    const { investigationEvidences } = investigation;

    if (investigationEvidences) {
      const alertsToLoad = (investigationEvidences?.journeyAlerts || [])
        .reduce((alertAccumulator, journeyAlert) => {
          // Create a list of new Alerts not already in state to be loaded in the Investigation detail view
          if (!journeyAlertsByToken[journeyAlert.journeyApplicationToken]) {
            alertAccumulator.push(journeyAlert);
          }

          return alertAccumulator;
        }, []);

      await dispatch(loadInvestigationJourneyAlerts({
        investigationToken,
        isMultiEntityAlertsInInvestigationsEnabled: Boolean(
          featureFlags?.multiEntityAlertsInInvestigations,
        ),
        journeyAlerts: alertsToLoad,
        typhonDomain,
      }));
    }
  },
);

export const loadSecondaryInvestigationData = createAsyncThunk(
  'investigations/loadSecondaryInvestigationData',
  async (
    {
      featureFlags,
      typhonDomain,
    }: SecondaryInvestigationDataRequest,
    { dispatch },
  ) => {
    try {
      const isEventLifecyclesPhaseThreeEnabled = Boolean(featureFlags?.eventLifecyclesPhaseThree);

      const [
        journeyActions,
        { outcomes },
        reasons,
        documentTypes,
      ] = await Promise.all([
        getJourneyActions({ typhonDomain, activeOnly: true }),
        getOutcomes({
          typhonDomain,
          options: {
            numRowsPerPage: OUTCOMES_API_MAX_LIMIT,
            q: {
              includeEventStatusOutcomes: isEventLifecyclesPhaseThreeEnabled,
            },
          },
        }),
        getReviewReasons(),
        getDocumentTypes({ typhonDomain }),
      ]);

      dispatch(mergeSecondaryInvestigationData({
        journeyActions,
        outcomes,
        reasons,
      }));

      if (documentTypes?.length > 0) {
        dispatch(mergeDocumentTypes(documentTypes));
      }
    } catch (error) {
      investigationsErrorHandler({
        error,
        extra: {
          typhonDomain,
        },
        message: 'Error loading secondary Investigation data',
      });
      dispatch(mergeInvestigationViewError(error));
    }
  },
);

export const loadAssignableAgents = createAsyncThunk(
  'investigations/loadAssignableAgents',
  async ({
    typhonDomain,
    investigationTypes,
    investigationStateTypes,
  }: AssignableAgentsRequest, { dispatch }) => {
    try {
      const assignableAgents = await fetchAssignableAgents({
        typhonDomain,
        investigationTypes,
        investigationStateTypes,
      });
      dispatch(mergeAssignableAgents(assignableAgents));
    } catch (error) {
      investigationsErrorHandler({
        error,
        extra: {
          typhonDomain,
        },
        message: 'Error fetching assignable agents',
      });
      dispatch(mergeInvestigationViewError(error));
    }
  },
);

export const loadJourneyAlertAssignableAgents = createAsyncThunk(
  'investigations/loadJourneyAlertAssignableAgents',
  async ({
    typhonDomain,
    journeyToken,
    journeyApplicationToken,
  }: JourneyAlertAssignableAgentsRequest, { dispatch }) => {
    try {
      const agents = await apiCalls.investigations.getAlertAssignableAgents({
        typhonDomain,
        journeyTokens: [journeyToken],
        journeyApplicationTokens: [journeyApplicationToken],
      });

      const formattedAssignableAgents = agents.map(({
        agentFirstName: firstName,
        agentLastName: lastName,
        agentToken: token,
      }) => ({
        firstName,
        lastName,
        token,
      }));

      dispatch(mergeJourneyAlertAssignableAgents({
        alertAssignableAgents: formattedAssignableAgents,
        journeyApplicationToken,
      }));
    } catch (error) {
      investigationsErrorHandler({
        error,
        extra: {
          typhonDomain,
        },
        message: 'Error fetching journey alert assignable agents',
      });
      dispatch(mergeInvestigationViewError(error));
    }
  },
);

export const loadInvestigationView = createAsyncThunk(
  'investigations/loadInvestigationView',
  async (
    {
      featureFlags,
      investigationToken,
      isEmployee = false,
      navigate = null,
      permissions = {},
      typhonDomain,
    }: any,
    { dispatch },
  ) => {
    // Users with CREATE permission can also read evidence
    const canReadEvidence = !!permissions?.['READ:Evidence:null']
      || !!permissions?.['CREATE:Evidence:null'];
    const [
      investigationThunk,
    ] = await Promise.all([
      dispatch(loadInvestigation({
        navigate,
        investigationToken,
        isEmployee,
        typhonDomain,
        featureFlags,
      })),
      dispatch(loadSecondaryInvestigationData({
        typhonDomain,
        featureFlags,
      })),
      // Only load evidence if feature flag is enabled AND user can read evidence
      ...(featureFlags?.addEvidenceToTrxsAndBas && canReadEvidence ? [
        dispatch(loadInvestigationEvidence({
          investigationToken,
          typhonDomain,
        })),
      ] : []),
    ]);

    const investigation = unwrapResult(investigationThunk);

    const { investigationType, investigationState } = investigation;

    await dispatch(loadAssignableAgents({
      typhonDomain,
      investigationTypes: [investigationType],
      investigationStateTypes: [investigationState],
    }));

    if (investigation?.investigationEvidences) {
      await dispatch(loadInvestigationJourneyAlerts({
        investigationToken,
        isMultiEntityAlertsInInvestigationsEnabled: Boolean(
          featureFlags?.multiEntityAlertsInInvestigations,
        ),
        journeyAlerts: investigation.investigationEvidences.journeyAlerts,
        typhonDomain,
      }));
    }
  },
);

export const assignAgentToInvestigation = createAsyncThunk(
  'investigations/assignAgent',
  async (
    { typhonDomain, payload }: AssignAgentRequest,
    { dispatch },
  ) => {
    try {
      const assignedAgent = await assignInvestigationAgent({ typhonDomain, payload });
      dispatch(mergeAssignedAgent(assignedAgent));
    } catch (error) {
      investigationsErrorHandler({
        error,
        extra: {
          typhonDomain,
        },
        message: 'Error assigning agent to Investigation',
      });
      dispatch(mergeInvestigationViewError(error));
    }
  },
);

export const assignAgentToAlert = createAsyncThunk(
  'investigations/assignAlertAgent',
  async (
    {
      investigationToken,
      journeyApplicationToken,
      typhonDomain,
      payload,
    }: AssignAlertAgentRequest,
    { dispatch },
  ) => {
    try {
      const assignedAgent = await assignAlertAgent({
        investigationToken,
        journeyApplicationToken,
        typhonDomain,
        payload,
      });
      dispatch(mergeAlertAssignedAgent({ journeyApplicationToken, assignedAgent }));
    } catch (error) {
      investigationsErrorHandler({
        error,
        extra: {
          typhonDomain,
        },
        message: 'Error assigning agent to Investigation Alert',
      });
      dispatch(mergeInvestigationViewError(error));
    }
  },
);

export const unassignAgentFromAlert = createAsyncThunk(
  'investigations/unassignAlertAgent',
  async (
    {
      investigationToken,
      journeyApplicationToken,
      typhonDomain,
    }: UnassignAlertAgentRequest,
    { dispatch },
  ) => {
    try {
      const unassigned = await unassignAlertAgent({
        investigationToken,
        journeyApplicationToken,
        typhonDomain,
      });
      dispatch(mergeAlertUnassignedAgent({ journeyApplicationToken, unassigned }));
    } catch (error) {
      investigationsErrorHandler({
        error,
        extra: {
          typhonDomain,
        },
        message: 'Error unassigning agent from Investigation Alert',
      });
      dispatch(mergeInvestigationViewError(error));
    }
  },
);

export const unassignAgentFromInvestigation = createAsyncThunk(
  'investigations/unassign',
  async (
    { typhonDomain, payload }: UnassignInvestigation,
    { dispatch },
  ) => {
    try {
      await unassignInvestigation({ typhonDomain, payload });
      dispatch(mergeUnassignInvestigationAgent());
    } catch (error) {
      investigationsErrorHandler({
        error,
        extra: {
          typhonDomain,
        },
        message: 'Error unassigning Investigation',
      });
      dispatch(mergeInvestigationViewError(error));
    }
  },
);

export const completeAlertReview = createAsyncThunk(
  'investigations/completeAlertReview',
  async (
    {
      investigationToken,
      journeyApplicationToken,
      payload,
      typhonDomain,
    }: CompleteAlertReviewRequest,
    { dispatch },
  ) => {
    try {
      const response = await completeAlertReviewCall({
        investigationToken,
        journeyApplicationToken,
        payload,
        typhonDomain,
      });
      return response;
    } catch (error) {
      investigationsErrorHandler({
        error,
        extra: {
          typhonDomain,
        },
        message: 'Error completing review on Investigation Alert',
      });
      dispatch(mergeInvestigationViewError(error));
    }
    return false;
  },
);

export const completeAlertManualReview = createAsyncThunk(
  'investigations/completeAlertManualReview',
  async (
    {
      alertManualReviewToken,
      investigationToken,
      journeyApplicationToken,
      payload,
      typhonDomain,
    }: CompleteAlertManualReviewRequest,
  ) => {
    const response = await submitAlertManualReview({
      alertManualReviewToken,
      investigationToken,
      journeyApplicationToken,
      payload,
      typhonDomain,
    });
    return response;
  },
);
